/* assets/wls-hair-dictionary.js */

class WlsHairDictionary extends HTMLElement {
    constructor() {
      super();
  
      // Bindings
      this._onWindowLoad = this._onWindowLoad.bind(this);
      this._onWindowResize = this._onWindowResize.bind(this);
  
      this._onSearchInput = this._onSearchInput.bind(this);
  
      this._onPillChange = this._onPillChange.bind(this);
      this._onPillHeadClick = this._onPillHeadClick.bind(this);
      this._onDocClick = this._onDocClick.bind(this);
  
      this._cleanupFns = [];
      this._activeVideo = null;
    }
  
    connectedCallback() {
      // Refs
      this.$masonry = this.querySelector('.js-masonry');
      this.$cards = Array.from(this.querySelectorAll('.wls-hair-dictionary__bottom-tab-box'));
  
      this.$search = this.querySelector('#dictionary-search');
  
      this.$pillWrapper = this.querySelector('.js-pill-select');
      this.$pillHead = this.$pillWrapper?.querySelector('.wls-pill-select__head') || null;
      this.$pillDropdown = this.$pillWrapper?.querySelector('.wls-pill-select__dropdown') || null;
      this.$pillInputs = this.$pillDropdown
        ? Array.from(this.$pillDropdown.querySelectorAll('input[type="checkbox"]'))
        : [];
  
      // Guard
      if (!this.$pillWrapper || !this.$pillDropdown || !this.$cards.length) return;
  
      // Index cards (title/desc/tags)
      this._indexCards();
  
      // Init UI
      this._updateLabel();
      this._applyFilters();
  
      // Masonry/video sizing
      window.addEventListener('load', this._onWindowLoad);
      window.addEventListener('resize', this._onWindowResize);
  
      this._cleanupFns.push(() => window.removeEventListener('load', this._onWindowLoad));
      this._cleanupFns.push(() => window.removeEventListener('resize', this._onWindowResize));
  
      // Search
      if (this.$search) {
        this.$search.addEventListener('input', this._onSearchInput);
        this._cleanupFns.push(() => this.$search.removeEventListener('input', this._onSearchInput));
      }
  
      // Pills
      this.$pillInputs.forEach((input) => input.addEventListener('change', this._onPillChange));
      this._cleanupFns.push(() => this.$pillInputs.forEach((input) => input.removeEventListener('change', this._onPillChange)));
  
      // Dropdown toggle
      if (this.$pillHead) {
        this.$pillHead.addEventListener('click', this._onPillHeadClick);
        this._cleanupFns.push(() => this.$pillHead.removeEventListener('click', this._onPillHeadClick));
      }
  
      // Close dropdown on outside click
      document.addEventListener('click', this._onDocClick);
      this._cleanupFns.push(() => document.removeEventListener('click', this._onDocClick));
  
      // Videos
      this._initVideos();
    }
  
    disconnectedCallback() {
      this._cleanupFns.forEach((fn) => fn());
      this._cleanupFns = [];
      this._stopAllVideos();
    }
  
    /* =========================
       Indexing
    ========================= */
  
    _indexCards() {
      this.$cards.forEach((card) => {
        const title = (card.querySelector('.wls-hair-dictionary__bottom-tab-box-title')?.textContent || '').trim();
        const desc = (card.querySelector('.wls-hair-dictionary__bottom-tab-box-desc')?.textContent || '').trim();
        const tags = Array.from(card.querySelectorAll('.wls-hair-dictionary__bottom-tab-box-list'))
          .map((el) => (el.getAttribute('data-tag') || el.textContent || '').trim())
          .filter(Boolean);
  
        card.__wls = {
          titleLower: title.toLowerCase(),
          descLower: desc.toLowerCase(),
          tagsLower: tags.map((t) => t.toLowerCase()),
        };
      });
    }
  
    /* =========================
       Pills (Same logic as old inline JS)
    ========================= */
  
    _updateLabel() {
      if (!this.$pillHead || !this.$pillInputs.length) return;
  
      const checked = this.$pillInputs.filter((i) => i.checked && i.value !== 'view-all');
  
      if (checked.length === 0) {
        this.$pillHead.textContent = 'View All';
        return;
      }
  
      if (checked.length === 1) {
        this.$pillHead.textContent = checked[0].nextElementSibling?.textContent?.trim() || 'View All';
        return;
      }
  
      this.$pillHead.textContent = `Selected (${checked.length})`;
    }
  
    _onPillChange(e) {
        const input = e.target;
        if (!input) return;
      
        const viewAll = this.$pillInputs.find((i) => i.value === 'view-all');
        const nonViewAll = this.$pillInputs.filter((i) => i.value !== 'view-all');
      
        // If View All is checked, clear all other selections
        if (input.value === 'view-all' && input.checked) {
          nonViewAll.forEach((i) => (i.checked = false));
          this._updateLabel();
          this._applyFilters();
          return;
        }
      
        // If any tag is checked, disable View All
        if (input.value !== 'view-all' && input.checked) {
          if (viewAll) viewAll.checked = false;
        }
      
        const checkedNonViewAll = nonViewAll.filter((i) => i.checked);
      
        // If all tags are selected, reset to View All
        if (checkedNonViewAll.length === nonViewAll.length && nonViewAll.length > 0) {
          if (viewAll) viewAll.checked = true;
          nonViewAll.forEach((i) => (i.checked = false));
        }
      
        // If no tags are selected, fall back to View All
        if (checkedNonViewAll.length === 0) {
          if (viewAll) viewAll.checked = true;
        }
      
        this._updateLabel();
        this._applyFilters();
      }
      
  
    _onPillHeadClick(e) {
      e.stopPropagation();
      if (!this.$pillWrapper) return;
      this.$pillWrapper.classList.toggle('is-open');
    }
  
    _onDocClick(e) {
      if (!this.$pillWrapper) return;
      if (!this.contains(e.target)) this.$pillWrapper.classList.remove('is-open');
    }
  
    _getSelectedTagValues() {
      const set = new Set(
        this.$pillInputs
          .filter((i) => i.value !== 'view-all' && i.checked)
          .map((i) => i.value.toLowerCase())
      );
      return set;
    }
  
    /* =========================
       Search
    ========================= */
  
    _onSearchInput() {
      this._applyFilters();
    }
  
    /* =========================
       Filtering (DOM hide/show)
    ========================= */
  
    _applyFilters() {
      const q = (this.$search?.value || '').trim().toLowerCase();
  
      const viewAll = this.$pillInputs.find((i) => i.value === 'view-all');
      const selected = this._getSelectedTagValues();
      const isViewAll = !!(viewAll && viewAll.checked) || selected.size === 0;
  
      this.$cards.forEach((card) => {
        const idx = card.__wls || { titleLower: '', descLower: '', tagsLower: [] };
  
        const matchesSearch =
          q.length === 0 ||
          idx.titleLower.includes(q) ||
          idx.descLower.includes(q) ||
          idx.tagsLower.some((t) => t.includes(q));
  
        let matchesTags = true;
        if (!isViewAll) {
          matchesTags = Array.from(selected).some((val) => idx.tagsLower.includes(val));
        }
  
        const show = matchesSearch && matchesTags;
  
        // Use display, not [hidden]
        card.style.display = show ? '' : 'none';
  
        if (!show && card.classList.contains('js-video-box')) {
          const video = card.querySelector('.js-video');
          if (video) {
            video.pause();
            const overlay = card.querySelector('.js-video-toggle');
            if (overlay) overlay.classList.remove('is-hidden');
            if (this._activeVideo === video) this._activeVideo = null;
          }
        }
      });
  
      this._scheduleRelayout();
    }
  
    /* =========================
       Masonry
    ========================= */
  
    _resizeMasonryItem(item) {
      if (!item || !this.$masonry) return;
  
      if (window.innerWidth < 576) {
        item.style.gridRowEnd = 'auto';
        return;
      }
  
      const styles = getComputedStyle(this.$masonry);
      const rowHeight = parseInt(styles.gridAutoRows, 10);
      const rowGap = parseInt(styles.gap, 10);
  
      const height = item.scrollHeight;
      const rowSpan = Math.ceil((height + rowGap) / (rowHeight + rowGap));
  
      item.style.gridRowEnd = `span ${rowSpan}`;
    }
  
    _resizeAllMasonryItems() {
      if (!this.$masonry) return;
  
      const items = this.querySelectorAll('.js-masonry .wls-hair-dictionary__bottom-tab-box');
      items.forEach((item) => {
        if (item.style.display === 'none') return;
        this._resizeMasonryItem(item);
      });
  
      this.$masonry.classList.add('is-loaded');
    }
  
    _fixVideoHeights() {
      const width = window.innerWidth;
      let height;
  
      if (width < 576) height = '378px';
      else if (width >= 576 && width < 991) height = '280px';
      else height = '373px';
  
      this.querySelectorAll('.js-video-box .wls-hair-dictionary__bottom-tab-box-video').forEach((el) => {
        el.style.height = height;
      });
  
      this.querySelectorAll('.js-video-box .js-video').forEach((el) => {
        el.style.height = height;
      });
  
      this.querySelectorAll('.js-video-box .video-overlay').forEach((el) => {
        el.style.height = height;
      });
    }
  
    _scheduleRelayout() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._fixVideoHeights();
          this._resizeAllMasonryItems();
        });
      });
    }
  
    _attachMediaRelayoutListeners() {
      this.querySelectorAll('.js-masonry img').forEach((img) => {
        if (!img.complete) {
          img.addEventListener('load', () => this._scheduleRelayout(), { once: true });
        }
      });
  
      this.querySelectorAll('.js-masonry video').forEach((video) => {
        if (video.readyState >= 1) {
          this._scheduleRelayout();
        } else {
          video.addEventListener('loadedmetadata', () => this._scheduleRelayout(), { once: true });
        }
        video.addEventListener('loadeddata', () => this._scheduleRelayout());
      });
    }
  
    _onWindowLoad() {
      this._fixVideoHeights();
      this._resizeAllMasonryItems();
      this._attachMediaRelayoutListeners();
    }
  
    _onWindowResize() {
      this._scheduleRelayout();
    }
  
    /* =========================
       Videos
    ========================= */
  
    _initVideos() {
      const videoBoxes = this.querySelectorAll('.js-video-box');
  
      videoBoxes.forEach((box) => {
        const video = box.querySelector('.js-video');
        const overlay = box.querySelector('.js-video-toggle');
        if (!video || !overlay) return;
  
        const playVideo = () => {
          if (this._activeVideo && this._activeVideo !== video) {
            this._activeVideo.pause();
            const prevOverlay =
              this._activeVideo.closest('.js-video-box')?.querySelector('.js-video-toggle') || null;
            if (prevOverlay) prevOverlay.classList.remove('is-hidden');
          }
  
          video.play();
          overlay.classList.add('is-hidden');
          this._activeVideo = video;
        };
  
        const pauseVideo = () => {
          video.pause();
          overlay.classList.remove('is-hidden');
          if (this._activeVideo === video) this._activeVideo = null;
        };
  
        overlay.addEventListener('click', playVideo);
        video.addEventListener('click', () => (video.paused ? playVideo() : pauseVideo()));
        video.addEventListener('ended', pauseVideo);
      });
    }
  
    _stopAllVideos() {
      this.querySelectorAll('.js-video').forEach((video) => {
        video.pause();
        const overlay = video.closest('.js-video-box')?.querySelector('.js-video-toggle') || null;
        if (overlay) overlay.classList.remove('is-hidden');
      });
      this._activeVideo = null;
    }
  }
  
  if (!customElements.get('wls-hair-dictionary')) {
    customElements.define('wls-hair-dictionary', WlsHairDictionary);
  }
  